import { Injectable, inject } from "@angular/core";
import {
    applicationStringFromApplicationType,
    IBaseCard,
    isIFirmTaxprepT1Card,
    isJobsBilling,
    isJobsBillingCard,
    productStringFromApplicationType,
} from "@app/views-interfaces";
import mixpanel from "mixpanel-browser";
import { firstValueFrom } from "rxjs";
import { AnalyticsTrackData } from "src/app/views-interfaces/analytics.interface";
import { AppSettingsService } from "../app-settings-service/app-settings.service";
import { AuthService } from "../auth/auth.service";
import { DashboardsService } from "../dashboards-service/dashboards.service";
import { FirmsService } from "../firms-service/firms.service";
import { IframeMessagesService } from "../iframe-messages-service/iframe-messages.service";
import { AppVersionService } from "./../app-version-service/app-version.service";

@Injectable({
    providedIn: "root",
})
export class AnalyticsService {
    private readonly appSettingsService = inject(AppSettingsService);
    private readonly authService = inject(AuthService);
    private readonly appVersionService = inject(AppVersionService);
    private readonly firmsService = inject(FirmsService);
    private readonly iframeMessagesService = inject(IframeMessagesService);
    private readonly dashboardsService = inject(DashboardsService);

    private isMixpanelIdentified = false;

    constructor() {}

    init(): void {
        setTimeout(() => {
            this.initAnalytics();
        }, 50);
    }
    private async initAnalytics(): Promise<void> {
        await firstValueFrom(this.authService.fetchedUserInfoEvent$);
        if (!this.isMixpanelIdentified) this.initMixPanel();
    }

    private mixPanelIdentify(): void {
        if (!this.isMixpanelIdentified) {
            mixpanel.identify(this.authService.currentUser?.sub);
            mixpanel.people.set({
                firmId: this.authService.currentUser?.firm_id,
            });
            this.isMixpanelIdentified = true;
        }
    }

    track(title: string, extra?: Record<string, unknown>): void {
        this.appVersionService.getVersion().subscribe(async (version: string) => {
            try {
                const currentDashboard = await this.dashboardsService.getCurrentDashboard();
                let data: AnalyticsTrackData = {
                    appVersion: version,
                    firmId: this.authService.currentUser?.firm_id,
                    firmName: (await this.firmsService.getFirmById(this.authService.currentUser.firm_id)).Name,
                    ctx_siteUrl: this.iframeMessagesService.messagesOrigin,
                    ctx_application: applicationStringFromApplicationType(currentDashboard?.ApplicationType),
                    ctx_product: productStringFromApplicationType(currentDashboard?.ApplicationType),
                    ...(!isJobsBilling(currentDashboard?.ApplicationType) && { ctx_year: currentDashboard?.TaxYear }),
                    dashboardId: currentDashboard?.Id,
                    ifirm_session_name: this.authService.currentUser?.ifirm_session_name
                        ? this.authService.currentUser.ifirm_session_name
                        : this.authService.sessionData.sessionName,
                    ifirm_userid: this.authService.currentUser?.ifirm_userid,
                };
                if (extra) {
                    data = Object.keys(extra).reduce((acc: AnalyticsTrackData, key: string) => {
                        acc[key] = extra[key];
                        return acc;
                    }, data);
                }
                if (this.isMixpanelIdentified) this.trackMixPanel(title, data);
            } catch (e: unknown) {
                // maybe log stuff?
            }
        });
    }

    private trackMixPanel(title: string, extra?: Record<string, unknown>): void {
        mixpanel.track(title, extra);
    }

    initMixPanel(): void {
        if (!this.appSettingsService.appSettings.Mixpanel_Key) return;
        mixpanel.init(this.appSettingsService.appSettings.Mixpanel_Key, { track_pageview: true, persistence: "localStorage" });
        this.mixPanelIdentify();
    }
}

export function getAnalyticsExtrasFromCard(card: IBaseCard | null, extra: Record<string, unknown> = {}) {
    if (card) {
        extra.cardId = card.cardId;
        if (isJobsBillingCard(card)) {
            extra.ctx_documentId = card.return.documentId;
            extra.ctx_taxpayerId = card.return.returnId;
            extra.jobId = card.job.id;
        } else if (isIFirmTaxprepT1Card(card)) {
            extra.ctx_documentId = card.documentId;
            extra.ctx_taxpayerId = card.return.id;
        } else {
            extra.ctx_taxpayerId = card.return.id;
        }
    }
    return extra;
}
